import { useState, useEffect, useRef, useCallback, useLayoutEffect, createContext, useContext } from "react";
import { useIsDeveloper } from '@/systems/Authentication'
import { Icon } from '@/components/Icon'
import { NavLink, useNavigation, useActionData, useNavigate, useLocation } from 'react-router'
import { Button, RoundIconButton } from '@/form/FormComponents'
import { useErrorModal, useHelpModal, useIsModalOpen } from '@/components/Modal';
import { useMenu } from '@/components/Menu';
import { useFormSubmit } from '@/form/FormProvider';
import { useResizeEffect, useIsMobile } from '@/hooks/useIsMobile';

export function useIsFormSubmitting() {
  const navigation = useNavigation();

  let isRedirecting =
  navigation.state === "loading" &&
  (navigation.formData != null || navigation.json != null) &&
  navigation.formAction !== navigation.location.pathname;

  return isRedirecting || navigation.state === "submitting";
}

export function useIsReloading() {
  const navigation = useNavigation();

  return navigation.state === "loading" && 
         navigation.location.pathname === window.location.pathname;
}

export function LoadingBar() {
  const navigation = useNavigation();
  const [fresh, setFresh] = useState(true);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const isDeveloper = useIsDeveloper();

  let loading = navigation.state === "loading" || navigation.state === "submitting";

  useEffect(function() {
    if (loading) {
      let still_loading = true;
      window.requestAnimationFrame(function() {
        window.requestAnimationFrame(function() {
          if (still_loading) {
            setLoaderVisible(true);
            setFresh(false);
          }
        })
      })
      return function() {
        still_loading = false;
      }
    } else {
      setFresh(true)
    }
  }, [loading])

  if (loading && fresh && loaderVisible) {
    setLoaderVisible(false);
  }

  let style;
  if (loading) {
    if (fresh) {
      style = {
        transition: "none",
        transform: "translateX(0%)"
      }
    } else {
      style = {
        transition: "",
        transform: "translateX(40%)"
      }
    }
  } else {
    style = {
      transform: ""
    }
  }

  if (!loaderVisible || (loading && fresh)) {
    style.visibility = "hidden";
  }

  return <>
    <div style={style} className={"loader" + (loading ? "" : " loader_complete")}></div>
    {isDeveloper ? <div className="developer_mode_indicator">Developer mode</div> : null}
  </>
}

const SidebarHeaderContext = createContext(null);

export function SidebarHeaderProvider({children}) {
  const [sidebar_open, setSidebarOpen] = useState(false);

  return (
    <SidebarHeaderContext.Provider value={{sidebar_open, setSidebarOpen}}>
      {children}
    </SidebarHeaderContext.Provider>
  )
}

export function Header({title, backButton, tabs, activeTab, setActiveTab, no_shadow = false, className, children}) {
  const navigate = useNavigate();
  const sidebarHeader = useContext(SidebarHeaderContext);
  const is_mobile = useIsMobile();
  const [buttons_hidden, setButtonsHidden] = useState(0);
  const menuItems = (children ? (children.length > 0 ? children : [children]) : []).filter(child => child?.type?.name === "Button").map(child => ({
    label: child.props.text,
    onClick: child.props.onClick,
    icon: child.props.icon,
    loading: child.props.loading
  }));
  const { handleOnClick } = useMenu({ menuItems: menuItems.slice(menuItems.length - buttons_hidden) });
  const actionsRef = useRef(null);
  const titleRef = useRef(null);
  const moreRef = useRef(null);

  const recalculate = useCallback(() => {
    if (actionsRef.current) {
      Array.from(actionsRef.current.children).forEach(child => {
        child.style.display = "";
      })
      moreRef.current.style.display = "none";

      let pend_buttons_hidden = 0;

      for (let i = 0; i < menuItems.length-1; i++) {

        titleRef.current.style.width = (titleRef.current.clientWidth - 1) + "px";
        titleRef.current.style.flex = "unset";

        const is_too_long = titleRef.current.scrollWidth > titleRef.current.clientWidth;

        titleRef.current.style.width = "";
        titleRef.current.style.flex = "";
        
        if (is_too_long) {
          Array.from(actionsRef.current.children)[menuItems.length - i - 1].style.display = "none";
          pend_buttons_hidden++;
          moreRef.current.style.display = "";
        }

      }

      setButtonsHidden(pend_buttons_hidden);
    }
  }, [menuItems.length])

  useLayoutEffect(() => {
    recalculate()
  }, [recalculate])

  useResizeEffect(() => {
    recalculate()
  })
    
  return (
    <div className={"dashboard_header" + (no_shadow ? " no_shadow" : "")}>
      {(backButton || title || children) ? <div className={"dashboard_header_inner " + (className || "")}>
        {backButton ? <CircularMenuButton onClick={() => navigate(backButton === true ? -1 : backButton)} icon="arrow-left"/> : null}
        {(sidebarHeader?.setSidebarOpen && is_mobile) ? <CircularMenuButton onClick={() => sidebarHeader?.setSidebarOpen(true)} icon="menu"/> : null}
        <div className="header_title" ref={titleRef}>{title}</div>
        <div className="header_actions" ref={actionsRef}>
          {children}
        </div>
        <div className="header_menu" ref={moreRef}><RoundIconButton icon="dots-vertical" onClick={handleOnClick}/></div>
      </div> : null}
      {tabs ? <div className="dashboard_header_tabs">
        {tabs.map((tab, index) => activeTab ? <div key={tab.id} className={"tab" + ((tab.id == activeTab) ? " active" : "")} tabIndex={0} onClick={() => {tab.onClick ? tab.onClick() : setActiveTab(tab.id)}}>{tab.icon && <Icon name={tab.icon}/>}<span>{tab.name}</span></div> : <NavLink key={index} to={tab.to} className="tab" end={tab.end}><span>{tab.name}</span></NavLink>)}
      </div> : null}
    </div>
  )
}

export function InlineHeader({title, subtitle, small, children}) {
  return (
    <div className={"dashboard_inline_header" + (small ? " small" : "")}>
      <div className="inline_header_title">
        <div>{title}</div>
        {subtitle ? <div>{subtitle}</div> : null}
      </div>
      <div className="inline_header_actions buttons">
        {children}
      </div>
    </div>
  )
}

export function InlineSection({children}) {
  return (
    <div className="dashboard_inline_section">
      {children}
    </div>
  )
}

export function Content({children}) {
  return (
    <div className="dashboard_content_inner nearest_scrollable_container">
      <div className="dashboard_content_container">
        {children}
      </div>
    </div>
  )
}

export function SidePanel({children}) {
  return (
    <div className="dashboard_side_panel">
      {children}
    </div>
  )
}

export function Sidebar({children, type="secondary", sidebar_open: sidebar_open_prop, setSidebarOpen: setSidebarOpen_prop}) {
  const sidebarHeader = useContext(SidebarHeaderContext);
  const sidebar_open = sidebarHeader?.sidebar_open || sidebar_open_prop || false;
  const setSidebarOpen = sidebarHeader?.setSidebarOpen || setSidebarOpen_prop || null;
  const [openHelpModal] = useHelpModal();
  const is_mobile = useIsMobile();
  const [was_open, setWasOpen] = useState(sidebar_open);
  const navigate = useNavigate();
  const navigation = useNavigation();
  const last_location = useRef(null);
  const location = useLocation();
  const is_modal_open = useIsModalOpen();
  const sidebarRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  // Set was_open to true after it's been opened once
  useEffect(() => {
    if (sidebar_open && !was_open) {
      setWasOpen(true);
    }
  }, [sidebar_open, was_open]);

  // Close sidebar when we're no longer mobile sized
  useEffect(() => {
    if (!is_mobile) {
      setWasOpen(false);
      setSidebarOpen?.(false);
    }
  }, [is_mobile, setSidebarOpen]);

  // Close sidebar when a modal opens
  useEffect(() => {
    if (is_modal_open) {
      setSidebarOpen?.(false);
    }
  }, [is_modal_open, setSidebarOpen]);

  // Close sidebar when navigation state changes to loading
  useEffect(() => {
    if (navigation.state === "loading") {
      setSidebarOpen?.(false);
    }
  }, [navigation, setSidebarOpen]);

  // Whenever we needed to load in order to navigate somewhere, store it so we don't trigger closing the sidebar when navigation finishes
  useEffect(() => {
    if (navigation.state === "loading" && navigation.location?.pathname) {
      last_location.current = navigation.location?.pathname;
    }
  }, [navigation]);

  // Close sidebar when a navigation finishes, but only if it didn't need to load
  useEffect(() => {
    if (location.pathname !== last_location.current) {
      setSidebarOpen?.(false);
    }
  }, [location, last_location, setSidebarOpen]);

  // Reset scroll position when the sidebar is opened
  useEffect(() => {
    if (sidebar_open) {
      const selected = sidebarRef.current.querySelector(".sidebar_button.selected");
      if (selected) {
        requestAnimationFrame(() => {
          selected.parentElement.scrollIntoViewIfNeeded();
        });
      } else {
        sidebarRef.current.scrollTop = 0;
      }
    }
  }, [sidebar_open]);

  // Focus management and focus trapping
  useEffect(() => {
    if (is_mobile && sidebar_open) {
      // Store the currently focused element
      lastFocusedElementRef.current = document.activeElement;
      
      // Focus the first focusable element in the sidebar
      const sidebar = sidebarRef.current;
      if (sidebar) {
        const focusableElements = sidebar.querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusable = focusableElements[0];
        firstFocusable?.focus();
      }
    } else if (is_mobile && !sidebar_open && lastFocusedElementRef.current) {
      // Return focus to the previously focused element when sidebar closes
      lastFocusedElementRef.current?.focus();
      lastFocusedElementRef.current = null;
    }
  }, [is_mobile, sidebar_open]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    if (!is_mobile || !sidebar_open) return;

    const handleKeyDown = (event) => {
      // Close sidebar on Escape key
      if (event.key === 'Escape') {
        setSidebarOpen?.(false);
        return;
      }

      // Focus trapping - handle Tab key
      if (event.key === 'Tab') {
        const sidebar = sidebarRef.current;
        if (!sidebar) return;

        const focusableElements = sidebar.querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
        );
        
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab - if we're on the first focusable element, wrap to last
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable?.focus();
          }
        } else {
          // Tab - if we're on the last focusable element, wrap to first
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [is_mobile, sidebar_open, setSidebarOpen]);

  return (
    <>
      {(is_mobile && (was_open || sidebar_open)) ? <div className={`sidebar_background modal_background ${(sidebar_open ? " modal_background_in" : " modal_background_out")}`} onClick={() => {setSidebarOpen?.(false)}} inert={!sidebar_open ? "true" : undefined}></div> : null}
      <nav className={"dashboard_sidebar" + (is_mobile ? (" mobile" + (sidebar_open ? " sidebarIn" : (was_open ? " sidebarOut" : ""))) : "") + (type === "secondary" ? " secondary" : "")} inert={(is_mobile && !sidebar_open) ? "true" : undefined} ref={sidebarRef}>
        {is_mobile && <div className="dashboard_sidebar_top_mobile">
          <CircularMenuButton onClick={() => {setSidebarOpen?.(false)}} icon="menu"/>
          {type === "main" && (
            <a className="logo" onClick={() => navigate("/")}>
              <Icon name="Lockup"/>
            </a>
          )}
        </div>}
        <section>
          <ul>
            {children}
          </ul>
        </section>
        {type === "main" && (
          <div className="dashboard_sidebar_footer">
            <div>© 2025 Sapient Clinician LLC</div>
            <div><a onClick={openHelpModal} tabIndex={0}>Help</a><a href="https://sapientclinician.com/privacy" target="_blank">Privacy</a><a href="https://sapientclinician.com/terms" target="_blank">Terms</a></div>
          </div>
        )}
      </nav>
    </>
  )
}

export function SidebarButton({to,icon,name,selected,onClick,indent,big,outlined,disabled,shimmer,end,replace}) {
  const sidebarHeader = useContext(SidebarHeaderContext);
  const handleClick = (e) => {
    sidebarHeader?.setSidebarOpen?.(false);
    onClick?.(e);
  }
  shimmer = false;
  return (
    <li>
      {to ?
        <NavLink to={to} end={end} replace={replace} className={({isActive}) => "sidebar_button" + ((selected != undefined && selected !== "default") ? (selected ? " selected" : "") : (isActive ? " selected" : "")) + (indent ? " indent-"+indent : "") + (big ? " big" : "") + (outlined ? " outlined" : "")}>
          {icon ? <Icon name={icon}/> : null}
          <span>{name}</span>
        </NavLink>
      :
        <a className={"sidebar_button" + (selected ? " selected" : "") + (indent ? " indent-"+indent : "") + (big ? " big" : "") + (outlined ? " outlined" : "") + (disabled ? " disabled" : "") + (shimmer ? " ai_shimmer ai_shimmer_"+shimmer : "")} tabIndex={0} inert={disabled ? "true" : undefined} onClick={handleClick}>
          {icon ? <Icon name={icon}/> : null}
          <span>{name}</span>
        </a>
      }
    </li>
  )
}

export function SidebarSpacing() {
  return (
    <li className="sidebar_spacing"></li>
  )
}

export function Actionbar({title, children}) {
  return (
    <div className="dashboard_actionbar">
      <div className="dashboard_actionbar_inner">
        {children}
      </div>
    </div>
  )
}

export function ActionbarLeft({children}) {
  return (
    <div className="actionbar_left">
      {children}
    </div>
  )
}

export function ActionbarRight({children}) {
  return (
    <div className="actionbar_right">
      {children}
    </div>
  )
}

export function CircularMenuButton({icon, disabled, onClick}) {
  return (
    <a className={"circular_menu_button "+(disabled ? "disabled" : "")} tabIndex={disabled ? -1 : 0} onClick={disabled ? () => {} : onClick}>
      <Icon name={icon}/>
    </a>
  )
}

export const Scrollable = ({ children, depencencies = [], style = {}, ...props }) => {
  const scroller_ref = useRef();
  const [topShadowAmont, setTopShadowAmount] = useState(0);
  const [bottomShadowAmont, setBottomShadowAmount] = useState(0);
  const dashboardContentStyles = {
    "--top_shadow_amount": topShadowAmont,
    "--bottom_shadow_amount": bottomShadowAmont
  }
  useEffect(function() {
    let scroller = scroller_ref.current;
    if (scroller && !scroller.classList.contains(".nearest_scrollable_container")) {
      scroller = scroller.querySelector(".nearest_scrollable_container:not([hidden])");
    }
    if (scroller) {
      let scroller_inner = scroller.querySelector(".dashboard_content_container");
      const handleScrollEvent = () => {
        let scroller_shadow_offset_height = Array.from(scroller.querySelectorAll(".scroller_shadow_offset_height")).map(element => element.offsetHeight).reduce((a, b) => a + b, 0);
        setTopShadowAmount(Math.min(1, (scroller.scrollTop-scroller_shadow_offset_height)/14).toFixed(2));
        setBottomShadowAmount(Math.min(1, (scroller.scrollHeight - (scroller.scrollTop-scroller_shadow_offset_height) - scroller.clientHeight)/14).toFixed(2));
      }
      let scroll_event_listener = scroller.addEventListener('scroll', handleScrollEvent);
      let resize_event_listener = window.addEventListener('resize', handleScrollEvent);
      let content_resize_observer;
      let content_mutation_observer;
      if (scroller_inner) {
        content_resize_observer = new ResizeObserver(handleScrollEvent);
        content_resize_observer.observe(scroller_inner);
        content_mutation_observer = new MutationObserver(handleScrollEvent);
        content_mutation_observer.observe(scroller_inner, { 
          childList: true, 
          subtree: true, 
          attributes: true,
          characterData: true
        });
      }
      handleScrollEvent();
      return function() {
        scroller.removeEventListener('scroll', scroll_event_listener);
        window.removeEventListener('resize', resize_event_listener);
        if (content_resize_observer) {
          content_resize_observer.disconnect();
        }
        if (content_mutation_observer) {
          content_mutation_observer.disconnect();
        }
      }
    } else {
      setTopShadowAmount(0);
      setBottomShadowAmount(0);
    }
  }, [...depencencies]);

  return (
    <div {...props} ref={scroller_ref} style={{ ...style, ...dashboardContentStyles }}>
      {children}
    </div>
  )
}

export function Pagination({show_back, show_continue, children, getContainerElement, autosave_indicator}) {
  function handlePrevious() {
    getContainerElement().querySelector(".sidebar_button.selected").parentElement.previousElementSibling.children[0].click()
  }

  function handleNext() {
    getContainerElement().querySelector(".sidebar_button.selected").parentElement.nextElementSibling.children[0].click()
  }

  return (
    <Actionbar>
      <ActionbarLeft>
        {null /* {show_back ? <Button text="Back" icon="chevron-left" onClick={handlePrevious}/> : null} */}
        {autosave_indicator ? <AutosaveIndicator/> : null}
      </ActionbarLeft>
      <ActionbarRight>
        {show_continue ? <Button text="Continue" className="primary" icon="chevron-right" icon_right={true} onClick={handleNext}/> : children}
      </ActionbarRight>
    </Actionbar>
  )
}

export function AutosaveIndicator() {
  const { autosave_status, has_changed } = useFormSubmit();

  return (
    <div className="autosave_indicator">
      {
        (autosave_status == "saving" || (has_changed && autosave_status !== "error")) ? <>
          <Icon name="sync-custom"/>
          <span>Saving...</span>
        </> : (
          autosave_status == "saved" ? <>
            <Icon name="cloud-check-variant-outline"/>
            <span>Saved</span>
          </> : (
            autosave_status == "error" ? <>
              <Icon name="sync-alert"/>
              <span>Error saving</span>
            </> : null
          )
        )
      }
    </div>
  )
}

export function useHandleSubmitError() {
  const failed = useActionData()?.success === false;
  const failed_date = useActionData()?.date;
  
  const [openErrorModal] = useErrorModal();

  useEffect(function() {
    if (failed) {
      openErrorModal();
    }
  }, [failed, failed_date])
}
